import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { TextField, Typography, Button, Paper } from '@material-ui/core';
import red from '@material-ui/core/colors/red';
import axios from 'axios';

import { useMutation } from '@apollo/client';
import { SignInMutation } from '../mutations/SignIn';
import { useUser } from '../contexts/user-context';

import { SIGNIN_WITH_GOOGLE_URL } from '../constants';
import { useStyles } from '../theme';

const styles = (theme) => ({
  root: {
    ...theme.mixins.gutters(),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  error: {
    backgroundColor: red[300]
  }
});

export const SignInForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const [signInUser, { data, error }] = useMutation(SignInMutation);
  const { signIn, user } = useUser();

  const classes = useStyles(styles);

  const redirectAfterSignIn = useCallback(() => {
    const from = location.state?.from || { pathname: '/' };
    navigate(from.pathname);
  }, [navigate, location]);

  useEffect(() => {
    if (error) {
      alert('There was a problem signing you in. Check your credentials.');
      return;
    }

    if (data && data.signIn) {
      signIn({ token: data.signIn.token, userData: data.signIn.user });
      redirectAfterSignIn();
    }
  }, [error, data, signIn, redirectAfterSignIn]);

  useEffect(() => {
    if (user && user.id) {
      redirectAfterSignIn();
    }
  }, [user, redirectAfterSignIn]);

  const handleFieldChange = (event) => {
    const { id, value } = event.target;
    if (id === 'email') {
      setEmail(value);
    } else if (id === 'password') {
      setPassword(value);
    }
  };

  const handleGoogleResponse = (response) => {
    if (response.error) {
      alert('Failed to sign in with Google!');
    } else {
      const tokenBlob = new Blob([
        JSON.stringify(
          {
            credential: response.credential
          },
          null,
          2
        )
      ]);
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Access-Control-Allow-Origin': '*'
        }
      };
      axios
        .post(SIGNIN_WITH_GOOGLE_URL, tokenBlob, config)
        .then((res) => {
          if (res.data.signIn.token) {
            signIn({
              token: res.data.signIn.token,
              userData: res.data.signIn.user
            });
            redirectAfterSignIn();
          } else {
            alert("User couldn't be found.");
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    signInUser({
      variables: { email, password }
    });
  };

  return (
    <Paper className={classes.root} square>
      <form onSubmit={handleSubmit}>
        <TextField
          required
          id="email"
          label="Email"
          className={classes.textField}
          margin="normal"
          fullWidth
          value={email}
          onChange={handleFieldChange}
        />
        <TextField
          required
          id="password"
          label="Password"
          className={classes.textField}
          margin="normal"
          type="password"
          fullWidth
          value={password}
          onChange={handleFieldChange}
        />
        <Typography align="right">
          <Button variant="outlined" color="primary" type="submit">
            Sign In
          </Button>
        </Typography>
        <Typography align="right">
          <div
            style={{
              'margin-left': 'auto',
              'margin-right': 0,
              'max-width': '200px',
              'margin-top': '.5em'
            }}
          >
            <GoogleLogin
              buttonText="Sign in with Google"
              onSuccess={handleGoogleResponse}
              onFailure={handleGoogleResponse}
              auto_select
              flow="auth-code"
            />
          </div>
        </Typography>
      </form>
    </Paper>
  );
};
